/**
 * EXIF Metadata Sanitizer for JPEG Files
 *
 * Removes specific problematic EXIF metadata tags from JPEG files to prevent
 * upload failures caused by malformed date formats.
 *
 * Problem:
 * Some JPEG sources produce EXIF date fields with "/" separators
 * (e.g., "2026/06/30 08:10:20") instead of the EXIF standard ":"
 * separator ("2026:06:30 08:10:20"). This causes downstream validation
 * failures in Smart.ly/Nucleus.
 *
 * Solution:
 * This module removes the targeted tags by setting their tag IDs to 0x0000,
 * which causes EXIF parsers to skip them entirely. The tags effectively
 * cease to exist from the parser's perspective.
 *
 * Targeted tags (removed):
 * - DateTime (0x0132) - in IFD0
 * - DateTimeOriginal (0x9003) - in EXIF sub-IFD
 * - DateTimeDigitized (0x9004) - in EXIF sub-IFD
 * - GPSLatitudeRef (0x0001) - in GPS IFD
 * - GPSLatitude (0x0002) - in GPS IFD
 * - GPSLongitudeRef (0x0003) - in GPS IFD
 * - GPSLongitude (0x0004) - in GPS IFD
 *
 * All other EXIF data (orientation, thumbnail, camera info) is preserved.
 */

const JPEG_SOI_MARKER = 0xffd8;
const JPEG_APP1_MARKER = 0xffe1;
const EXIF_HEADER = "Exif";

// All tags to remove (by nulling their tag ID)
const TAGS_TO_REMOVE: Set<number> = new Set([
  // DateTime tags
  0x0132, // DateTime (IFD0)
  0x9003, // DateTimeOriginal (EXIF sub-IFD)
  0x9004, // DateTimeDigitized (EXIF sub-IFD)
  // GPS tags
  0x0001, // GPSLatitudeRef
  0x0002, // GPSLatitude
  0x0003, // GPSLongitudeRef
  0x0004, // GPSLongitude
]);

// IFD0 pointer tags
const EXIF_IFD_POINTER_TAG = 0x8769;
const GPS_IFD_POINTER_TAG = 0x8825;

/**
 * Checks if a file is a JPEG based on its MIME type or file extension.
 */
export const isJpegFile = (file: File): boolean => {
  const mimeType = file.type.toLowerCase();
  if (mimeType === "image/jpeg" || mimeType === "image/jpg") {
    return true;
  }
  const extension = file.name.toLowerCase().split(".").pop();
  return extension === "jpg" || extension === "jpeg";
};

/**
 * Reads a File object into an ArrayBuffer.
 */
const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Reads a 16-bit unsigned integer respecting endianness.
 */
const readUint16 = (
  view: DataView,
  offset: number,
  littleEndian: boolean,
): number => {
  return view.getUint16(offset, littleEndian);
};

/**
 * Reads a 32-bit unsigned integer respecting endianness.
 */
const readUint32 = (
  view: DataView,
  offset: number,
  littleEndian: boolean,
): number => {
  return view.getUint32(offset, littleEndian);
};

/**
 * Removes a tag entry by setting its tag ID to 0x0000, which makes
 * EXIF parsers skip it entirely. The tag effectively ceases to exist.
 */
const removeTag = (
  bytes: Uint8Array,
  entryOffset: number,
  littleEndian: boolean,
): void => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  view.setUint16(entryOffset, 0, littleEndian);
};

/**
 * Processes an IFD, removing targeted tags by nulling their IDs.
 */
const processIfd = (
  bytes: Uint8Array,
  ifdOffset: number,
  littleEndian: boolean,
  tagsToRemove: Set<number>,
): void => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  if (ifdOffset + 2 > bytes.length) {
    return;
  }

  const entryCount = readUint16(view, ifdOffset, littleEndian);
  let offset = ifdOffset + 2;

  for (let i = 0; i < entryCount; i++) {
    if (offset + 12 > bytes.length) {
      return;
    }
    const tag = readUint16(view, offset, littleEndian);

    if (tagsToRemove.has(tag)) {
      removeTag(bytes, offset, littleEndian);
    }

    offset += 12;
  }
};

/**
 * Finds a sub-IFD pointer tag in an IFD and returns the absolute offset.
 * Returns -1 if the pointer tag is not found.
 */
const findSubIfdOffset = (
  bytes: Uint8Array,
  ifdOffset: number,
  tiffHeaderOffset: number,
  littleEndian: boolean,
  pointerTag: number,
): number => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  if (ifdOffset + 2 > bytes.length) {
    return -1;
  }

  const entryCount = readUint16(view, ifdOffset, littleEndian);
  let offset = ifdOffset + 2;

  for (let i = 0; i < entryCount; i++) {
    if (offset + 12 > bytes.length) {
      return -1;
    }
    const tag = readUint16(view, offset, littleEndian);

    if (tag === pointerTag) {
      const subIfdRelativeOffset = readUint32(view, offset + 8, littleEndian);
      return tiffHeaderOffset + subIfdRelativeOffset;
    }

    offset += 12;
  }

  return -1;
};

/**
 * Locates the APP1 EXIF segment in a JPEG buffer and returns its start offset.
 * Returns -1 if not found.
 */
const findExifSegmentOffset = (view: DataView): number => {
  let offset = 2; // Skip SOI

  while (offset < view.byteLength - 1) {
    if (view.getUint8(offset) !== 0xff) {
      return -1;
    }

    const marker = view.getUint16(offset);

    if (marker === 0xffda) {
      return -1;
    }

    // Standalone markers (no length field)
    if (
      (marker >= 0xffd0 && marker <= 0xffd9) ||
      marker === 0xff01
    ) {
      offset += 2;
      continue;
    }

    if (offset + 4 > view.byteLength) {
      return -1;
    }

    const segmentLength = view.getUint16(offset + 2);

    if (marker === JPEG_APP1_MARKER && offset + 10 <= view.byteLength) {
      const header = String.fromCharCode(
        view.getUint8(offset + 4),
        view.getUint8(offset + 5),
        view.getUint8(offset + 6),
        view.getUint8(offset + 7),
      );
      if (header === EXIF_HEADER) {
        return offset;
      }
    }

    offset += 2 + segmentLength;
  }

  return -1;
};

/**
 * Removes targeted EXIF tags from a JPEG buffer by nulling their tag IDs.
 *
 * Returns a new ArrayBuffer with modifications applied.
 * Non-JPEG buffers or buffers without EXIF are returned as-is.
 */
export const normalizeExifInBuffer = (buffer: ArrayBuffer): ArrayBuffer => {
  const view = new DataView(buffer);

  if (view.byteLength < 2 || view.getUint16(0) !== JPEG_SOI_MARKER) {
    return buffer;
  }

  const exifSegmentOffset = findExifSegmentOffset(view);
  if (exifSegmentOffset === -1) {
    return buffer;
  }

  // Work on a copy to avoid mutating the original
  const copy = buffer.slice(0);
  const bytes = new Uint8Array(copy);

  // TIFF header starts after APP1 marker (2) + length (2) + "Exif\0\0" (6)
  const tiffHeaderOffset = exifSegmentOffset + 10;

  if (tiffHeaderOffset + 8 > bytes.length) {
    return buffer;
  }

  const tiffView = new DataView(copy, tiffHeaderOffset);

  // Determine endianness from TIFF header
  const byteOrder = tiffView.getUint16(0);
  let littleEndian: boolean;
  if (byteOrder === 0x4949) {
    littleEndian = true;
  } else if (byteOrder === 0x4d4d) {
    littleEndian = false;
  } else {
    return buffer;
  }

  // Verify TIFF magic number (42)
  if (readUint16(tiffView, 2, littleEndian) !== 42) {
    return buffer;
  }

  // Get IFD0 offset
  const ifd0RelativeOffset = readUint32(tiffView, 4, littleEndian);
  const ifd0Offset = tiffHeaderOffset + ifd0RelativeOffset;

  // Process IFD0 - remove DateTime tag
  processIfd(bytes, ifd0Offset, littleEndian, TAGS_TO_REMOVE);

  // Find and process EXIF sub-IFD - remove DateTimeOriginal, DateTimeDigitized
  const exifSubIfdOffset = findSubIfdOffset(
    bytes, ifd0Offset, tiffHeaderOffset, littleEndian, EXIF_IFD_POINTER_TAG,
  );
  if (exifSubIfdOffset > 0) {
    processIfd(bytes, exifSubIfdOffset, littleEndian, TAGS_TO_REMOVE);
  }

  // Find and process GPS IFD - remove GPS location tags
  const gpsIfdOffset = findSubIfdOffset(
    bytes, ifd0Offset, tiffHeaderOffset, littleEndian, GPS_IFD_POINTER_TAG,
  );
  if (gpsIfdOffset > 0) {
    processIfd(bytes, gpsIfdOffset, littleEndian, TAGS_TO_REMOVE);
  }

  return copy;
};

/**
 * Normalizes EXIF metadata in a JPEG File object.
 * Fixes malformed date separators and removes GPS location tags.
 * Non-JPEG files are returned unchanged.
 *
 * @param file - The file to process
 * @returns A new File with normalized EXIF (for JPEGs) or the original file
 */
export const stripExifFromFile = async (file: File): Promise<File> => {
  if (!isJpegFile(file)) {
    return file;
  }

  const buffer = await readFileAsArrayBuffer(file);
  const normalizedBuffer = normalizeExifInBuffer(buffer);

  if (normalizedBuffer === buffer) {
    return file;
  }

  return new File([normalizedBuffer], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
};

/**
 * Processes an array of files, normalizing EXIF in any JPEG files.
 * Non-JPEG files pass through unchanged.
 *
 * @param files - Array of files to process
 * @returns Array of files with normalized EXIF metadata
 */
export const stripExifFromFiles = async (files: File[]): Promise<File[]> => {
  return Promise.all(files.map(stripExifFromFile));
};
